import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import {
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';

import Text from '~/common/componentMapper/Text';
import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import IconButton from '~/common/components/Icons/IconButton';
import Input from '~/common/components/Inputs/Input';
import InputGroup from '~/common/components/Inputs/InputGroup';
import InputRightElement from '~/common/components/Inputs/InputRightElement';
import Select from '~/common/components/Selects/Select';
import theme from '~/theme';

import type { DocumentOption } from './types';

type ToolbarProps = {
  sx?: any;
  docName?: string;
  documentId?: number;
  zoom?: number;
  scale?: number;
  onScaleChange?: (scale: number) => unknown;
  minZoom?: number;
  maxZoom?: number;
  currentPageNumber?: number;
  totalPages?: number;
  setPageNumberChange?: (pageNumber: number) => unknown;
  documentOptions?: Array<DocumentOption>;
};

function Toolbar({
  documentId,
  zoom,
  scale,
  onScaleChange,
  minZoom,
  maxZoom,
  currentPageNumber,
  totalPages,
  setPageNumberChange,
  documentOptions,
}: Readonly<ToolbarProps>) {
  const ref = useRef<{ value: number }>();

  let zoomChangeTimer: ReturnType<typeof setTimeout>;

  const topOption = { id: 0, label: 'View Document' };
  let documentOptionsList = [];
  if (documentOptions.length > 0) {
    documentOptionsList = [topOption, ...documentOptions];
  }

  const [selectedOption, setSelectedOption] = useState(
    documentOptionsList[0]?.id,
  );

  useEffect(() => {
    setSelectedOption(documentId);
  }, [documentId]);

  useEffect(() => {
    ref.current.value = zoom;
  }, [zoom]);

  function handleZoomChange(e) {
    const percent = e.target.value;
    clearTimeout(zoomChangeTimer);
    zoomChangeTimer = setTimeout(() => {
      onScaleChange(percent / 100);
    }, 500);
  }

  function handleZoomIn() {
    onScaleChange(scale + 0.1);
  }

  function handleZoomOut() {
    onScaleChange(scale - 0.1);
  }

  function handlePDFOnFocus(e) {
    const documentId = [
      {
        documentId: Number(e.target.value),
      },
    ];
    window.postMessage({ type: 'pdfCitation', payload: documentId });
  }
  return (
    <Box
      style={{
        width: '100%',
        background: theme.colors?.gray[700],
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
      }}
    >
      <Flex style={{ alignItems: 'center', width: '100%' }}>
        {documentOptionsList && documentOptionsList.length > 0 && (
          <Box
            sx={{
              pl: '10px',
              bg: theme.colors?.gray[700],
            }}
          >
            <Select
              aria-label="Select Document"
              value={selectedOption}
              iconColor="white"
              sx={{
                backgroundColor: 'black',
                color: 'white',
                width: '100%',
                minWidth: '180px',
              }}
              onChange={(e) => {
                handlePDFOnFocus(e);
              }}
            >
              {documentOptionsList.map(({ id, label }) => (
                <option
                  style={{ backgroundColor: 'black', color: 'white' }}
                  key={id}
                  value={id}
                >
                  {label}
                </option>
              ))}
            </Select>
          </Box>
        )}
      </Flex>
      <Flex sx={{ width: '100%' }}>
        {/* Pagination Controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setPageNumberChange(currentPageNumber - 1)}
            tabIndex={-1}
            colorScheme="transparent"
            disabled={currentPageNumber <= 1}
            aria-label="Previous Page"
          >
            <FontAwesomeIcon icon={faChevronLeft} style={{ color: 'white' }} />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Text sx={{ color: 'white', p: 1 }}>
              {currentPageNumber}/{totalPages}
            </Text>
          </Box>
          <IconButton
            onClick={() => setPageNumberChange(currentPageNumber + 1)}
            tabIndex={-1}
            colorScheme="transparent"
            outlineColor={'none'}
            disabled={currentPageNumber === totalPages}
            aria-label="Next Page"
          >
            <FontAwesomeIcon icon={faChevronRight} style={{ color: 'white' }} />
          </IconButton>
        </Box>
        {/* Zoom controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={handleZoomOut}
            tabIndex={-1}
            colorScheme="transparent"
            aria-label="Zoom Out"
          >
            <FontAwesomeIcon
              icon={faMagnifyingGlassMinus}
              style={{ color: 'white' }}
            />
          </IconButton>
          <Box sx={{ position: 'relative', margin: '1rem 0.5rem' }}>
            <InputGroup>
              <Flex align={'center'}>
                <Input
                  ref={ref}
                  min={minZoom}
                  max={maxZoom}
                  onChange={handleZoomChange}
                  type="number"
                  style={{
                    width: '5rem',
                    background: theme.colors?.gray[700],
                    color: 'white',
                  }}
                  tabIndex={-1}
                  aria-label="Zoom Level"
                />
                <InputRightElement style={{ color: 'white' }}>
                  %
                </InputRightElement>
              </Flex>
            </InputGroup>
          </Box>
          <IconButton
            onClick={handleZoomIn}
            tabIndex={-1}
            colorScheme="transparent"
            outlineColor={'none'}
            aria-label="Zoom In"
          >
            <FontAwesomeIcon
              icon={faMagnifyingGlassPlus}
              style={{ color: 'white' }}
            />
          </IconButton>
        </Box>
      </Flex>
    </Box>
  );
}

export default Toolbar;
